import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import {
  Badge,
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  InputBase,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Popper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  AutoAwesomeOutlined,
  BusinessOutlined,
  ChecklistRounded,
  DescriptionOutlined,
  HelpOutlineRounded,
  MailOutlineRounded,
  NotificationsNoneRounded,
  PersonAddAltOutlined,
  SearchRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { APPROVALS_KEY, approvalsApi } from '@/api/vendors.api';
import { HOTEL_APPROVALS_KEY, hotelReviewApi } from '@/api/hotels.api';
import { openAssistant } from '@/components/assistant/AssistantPanel';
import { COMPANY_EMAIL } from '@/config/productBrand';
import { useAuth } from '@/auth/useAuth';
import { navGroups, type NavItem } from './navConfig';

const SETTINGS_PAGES: NavItem[] = [
  { label: 'Bank accounts', to: '/settings/bank-accounts', perms: ['Company.View'] },
  { label: 'Invoice series', to: '/settings/invoice-series', perms: ['Company.View'] },
  { label: 'Quote, invoice and voucher templates', to: '/settings/documents', perms: ['Company.View'] },
  { label: 'KYC', to: '/settings/kyc', perms: ['Company.View'] },
  { label: 'Support access', to: '/settings/support', perms: ['Company.View'] },
  { label: 'Email (SMTP)', to: '/settings/email', perms: ['Company.View'] },
  { label: 'WhatsApp', to: '/settings/whatsapp', perms: ['Company.View'] },
];

/** Every screen a person can open from the menu, flattened for search. */
function usePages() {
  const { hasAnyPermission } = useAuth();
  return useMemo(() => {
    const pages: { label: string; to: string; group: string }[] = [];
    const add = (item: NavItem, group: string) => {
      if (item.to && (!item.perms?.length || hasAnyPermission(item.perms))) pages.push({ label: item.label, to: item.to, group });
    };
    for (const g of navGroups) {
      if (g.comingSoon) continue;
      if (g.to) add({ label: g.label, to: g.to, perms: g.perms }, g.label);
      g.children?.forEach((c) => add(c, g.label));
    }
    // Settings pages reached through the Settings tabs rather than the menu.
    for (const s of SETTINGS_PAGES) add(s, 'Settings');
    return pages;
  }, [hasAnyPermission]);
}

/** Header search (O11). Finds screens today; bookings, customers, vendors and invoices join as they ship. */
export function GlobalSearch() {
  const navigate = useNavigate();
  const pages = usePages();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLDivElement | null>(null);
  const input = useRef<HTMLInputElement | null>(null);

  // Ctrl K / ⌘K focuses the search from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); input.current?.focus(); setOpen(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const term = q.trim().toLowerCase();
  const results = term ? pages.filter((p) => `${p.label} ${p.group}`.toLowerCase().includes(term)).slice(0, 8) : [];
  const go = (to: string) => { setQ(''); setOpen(false); input.current?.blur(); navigate(to); };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchor} sx={{ position: 'relative', width: { md: 300, lg: 420 }, display: { xs: 'none', md: 'block' } }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 2, px: 1.25, height: 40, bgcolor: 'background.paper' }}
        >
          <SearchRounded fontSize="small" sx={{ color: 'text.secondary' }} />
          <InputBase
            inputRef={input}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && results[0]) go(results[0].to);
              if (e.key === 'Escape') { setOpen(false); input.current?.blur(); }
            }}
            placeholder="Search bookings, customers, vendors, invoices"
            inputProps={{ 'aria-label': 'Search' }}
            sx={{ flex: 1, fontSize: 14 }}
          />
          <Box sx={{ fontSize: 11, color: 'text.secondary', border: 1, borderColor: 'divider', borderRadius: 1, px: 0.75, lineHeight: '18px', whiteSpace: 'nowrap' }}>
            Ctrl K
          </Box>
        </Stack>
        <Popper open={open && term.length > 0} anchorEl={anchor.current} placement="bottom-start" sx={{ zIndex: (t) => t.zIndex.modal }}>
          <Paper elevation={6} sx={{ mt: 0.75, width: anchor.current?.offsetWidth ?? 360, borderRadius: 2, py: 0.5 }}>
            {results.map((r) => (
              <MenuItem key={r.to} onClick={() => go(r.to)}>
                <ListItemText primary={r.label} secondary={r.group === r.label ? undefined : r.group} />
              </MenuItem>
            ))}
            {results.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.25 }}>
                No screen matches “{q.trim()}”. Bookings, customers, vendors and invoices become searchable as those
                modules are switched on.
              </Typography>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

/** "+ New" (O11): what can be created from anywhere. Modules not built yet are listed but disabled. */
export function NewMenu() {
  const navigate = useNavigate();
  const { hasAnyPermission } = useAuth();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const close = () => setAnchor(null);
  const go = (to: string) => { close(); navigate(to); };

  return (
    <>
      <Button
        variant="contained"
        startIcon={<AddRounded />}
        onClick={(e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
        sx={{ minWidth: 0, px: { xs: 1.25, sm: 2 }, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } } }}
        aria-label="New"
      >
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>New</Box>
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 220, borderRadius: 2 } } }}
      >
        {hasAnyPermission(['User.Create', 'User.Edit']) && (
          <MenuItem onClick={() => go('/business-profile/staff')}>
            <ListItemIcon><PersonAddAltOutlined fontSize="small" /></ListItemIcon>
            Team member
          </MenuItem>
        )}
        {hasAnyPermission(['Branch.Create', 'Branch.Edit']) && (
          <MenuItem onClick={() => go('/business-profile/branches')}>
            <ListItemIcon><BusinessOutlined fontSize="small" /></ListItemIcon>
            Branch
          </MenuItem>
        )}
        {['Enquiry', 'Quote', 'Booking', 'Customer', 'Vendor'].map((label) => (
          <MenuItem key={label} disabled>
            <ListItemText primary={label} secondary="Coming soon" />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * Notifications bell (O11, FR-NAV-01/08): the count of approvals waiting (vendor KYC, rate changes) for people who
 * approve, with the list and a link to the Approvals centre. Other alerts join as their modules ship.
 */
export function NotificationsBell() {
  const navigate = useNavigate();
  const { hasAnyPermission } = useAuth();
  const canVendors = hasAnyPermission(['Vendor.Approve']);
  const canHotels = hasAnyPermission(['Hotel.Approve']);
  const canApprove = canVendors || canHotels;
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const count = useQuery({ queryKey: [APPROVALS_KEY, 'count'], queryFn: approvalsApi.count, enabled: canVendors, refetchInterval: 120_000, staleTime: 60_000 });
  const list = useQuery({ queryKey: [APPROVALS_KEY, 'list'], queryFn: approvalsApi.list, enabled: canVendors && Boolean(anchor) });
  const hotels = useQuery({ queryKey: [HOTEL_APPROVALS_KEY, 'list'], queryFn: hotelReviewApi.approvals, enabled: canHotels, refetchInterval: 120_000, staleTime: 60_000 });
  const n = (canVendors ? count.data ?? 0 : 0) + (canHotels ? hotels.data?.length ?? 0 : 0);
  const entries = [...(list.data?.items ?? []), ...(hotels.data ?? []).map((h) => ({ kind: h.kind, id: h.id, title: h.title, subtitle: h.subtitle }))];
  return (
    <>
      <Tooltip title={n ? `${n} waiting for approval` : 'Notifications'}>
        <IconButton aria-label="Notifications" onClick={(e) => setAnchor(e.currentTarget)}>
          <Badge color="error" badgeContent={n}>
            <NotificationsNoneRounded />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, width: 320, borderRadius: 2, p: 2 } } }}
      >
        <Typography variant="subtitle2" fontWeight={700}>Notifications</Typography>
        {n > 0 ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 1 }}>{n} waiting for your approval</Typography>
            <Stack spacing={0.5}>
              {entries.slice(0, 6).map((i) => (
                <Box key={`${i.kind}-${i.id}`} sx={{ px: 1, py: 0.75, borderRadius: 1, bgcolor: '#F7F8FA' }}>
                  <Typography fontSize={13} fontWeight={600}>{i.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{i.subtitle}</Typography>
                </Box>
              ))}
            </Stack>
            <Button fullWidth variant="contained" size="small" sx={{ mt: 1.5 }} onClick={() => { setAnchor(null); navigate('/approvals'); }}>Open Approvals centre</Button>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            You’re all caught up. New enquiries, bookings and reminders will appear here.
          </Typography>
        )}
      </Popover>
    </>
  );
}

/** Help (O11): the assistant, the guided setup, support and terms. */
export function HelpMenu() {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const close = () => setAnchor(null);
  return (
    <>
      <Tooltip title="Help">
        <IconButton aria-label="Help" onClick={(e) => setAnchor(e.currentTarget)}>
          <HelpOutlineRounded />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 230, borderRadius: 2 } } }}
      >
        <MenuItem onClick={() => { close(); openAssistant(); }}>
          <ListItemIcon><AutoAwesomeOutlined fontSize="small" /></ListItemIcon>
          Ask the assistant
        </MenuItem>
        <MenuItem onClick={() => { close(); navigate('/dashboard'); }}>
          <ListItemIcon><ChecklistRounded fontSize="small" /></ListItemIcon>
          Guided setup
        </MenuItem>
        <MenuItem component="a" href={`mailto:${COMPANY_EMAIL}`} onClick={close}>
          <ListItemIcon><MailOutlineRounded fontSize="small" /></ListItemIcon>
          Contact support
        </MenuItem>
        <MenuItem onClick={() => { close(); navigate('/terms'); }}>
          <ListItemIcon><DescriptionOutlined fontSize="small" /></ListItemIcon>
          Terms and privacy
        </MenuItem>
      </Menu>
    </>
  );
}
